import { CloseIcon } from '@diligentcorp/atlas-react-icons';
import { IssueSourceDtoEntityTypeEnum } from '@issuemanager/api-client';
import {
  ControlledTextField,
  LoadingButton,
} from '@issuemanager/apps/client/components';
import { useAddRelationship } from '@issuemanager/client/features/issues';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  useEventCallback,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import { ReactNode, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useSourceEntityTypes } from './components/use-source-entity-types';
import ObjectSelector from './object-selector';

interface AddRelationshipModalProps {
  isOpen: boolean;
  onClose: () => void;
}

function AddRelationshipModal({
  isOpen,
  onClose,
}: AddRelationshipModalProps): ReactNode {
  const { t } = useTranslation('issue');
  const { enqueueSnackbar } = useSnackbar();

  const labels = {
    type: t('form.relationships.type'),
    object: t('form.relationships.object'),
  };

  const defaultValues = useMemo(
    () => ({
      type: '',
      object: '',
    }),
    [],
  );

  const rules = {
    type: {
      required: t('form.errorMessages.type.required'),
    },
    object: {
      required: t('form.errorMessages.object.required'),
    },
  };

  const form = useForm<typeof defaultValues>({
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
    defaultValues,
  });

  const handleClose = (): void => {
    form.reset();
    onClose();
  };

  const selectedType = form.watch('type');
  const selectedObject = form.watch('object');

  const sourceEntityTypes = useSourceEntityTypes();
  const typeOptions = useMemo(
    () =>
      sourceEntityTypes.map(({ value, label }) => ({
        label,
        value,
      })),
    [sourceEntityTypes],
  );

  const addRelationshipMutation = useAddRelationship({
    onSuccess: () => {
      enqueueSnackbar(t('relationships.save.success'), {
        variant: 'success',
      });
    },
  });

  const onSubmit = async (values: typeof defaultValues): Promise<void> => {
    await addRelationshipMutation.mutateAsync(values);
    handleClose();
  };

  const handleSubmit = useEventCallback(() => {
    form.handleSubmit(onSubmit)();
  });

  return (
    <Dialog
      open={isOpen}
      aria-describedby='add-relationship-modal-content'
      aria-labelledby='add-relationship-modal-title'
      data-testid='confirmation-modal'
      onClose={handleClose}
    >
      <DialogTitle>
        {t('form.relationships.addRelationship')}
        <IconButton
          onClick={handleClose}
          aria-label='add-relationship-modal-close'
          color='inherit'
          data-testid='close-button'
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent data-testid='add-relationship-modal-content'>
        <Stack spacing={1}>
          <ControlledTextField
            name='type'
            label={labels.type}
            control={form.control}
            options={typeOptions}
            rules={rules.type}
            onChange={() => {
              form.resetField('object');
            }}
            data-testid='type'
          />
          {selectedType && (
            <ObjectSelector
              name='object'
              control={form.control}
              rules={rules.object}
              label={labels.object}
              selectedType={selectedType as IssueSourceDtoEntityTypeEnum}
            />
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button
          variant='outlined'
          data-testid='cancel-button'
          onClick={handleClose}
        >
          {t('actions.cancel')}
        </Button>
        <LoadingButton
          variant='contained'
          data-testid='accept-button'
          onClick={handleSubmit}
          disabled={!selectedType || !selectedObject}
          loading={addRelationshipMutation.isPending}
        >
          {t('form.relationships.add')}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}

export default AddRelationshipModal;
